import {
    Component,
    ChangeDetectionStrategy,
    computed,
    effect,
    input,
    model,
    OnInit,
    Injector,
    inject,
} from "@angular/core";
import { MatIcon } from "@angular/material/icon";

@Component({
    selector: "app-cerrix-pagination",
    imports: [MatIcon],
    templateUrl: "./cerrix-pagination.component.html",
    styleUrl: "./cerrix-pagination.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CerrixPaginationComponent implements OnInit {
    private readonly injector = inject(Injector);

    public totalItems = input.required<number>();
    public itemsPerPage = input.required<number>();
    public limit = input(7);

    public currentPage = model<number>();

    public pages = computed(() => {
        const total = this.totalItems();
        const perPage = this.itemsPerPage();
        return total && perPage && perPage > 0 ? Math.ceil(total / perPage) : 1;
    });

    public ghostSlots = computed(() =>
        Array.from({ length: Math.min(this.pages(), this.limit()) }, (_, i) => i),
    );

    public pagination = computed<number[]>(() => {
        const totalPages = this.pages();
        const currentPage = Math.min(Math.max(this.currentPage(), 1), totalPages);
        const limit = this.limit();

        if (totalPages <= 1) return [1];
        if (totalPages <= limit) return Array.from({ length: totalPages }, (_, i) => i + 1);

        const out: number[] = [];
        const add = (n: number) => {
            const last = out[out.length - 1];
            if (n !== last) out.push(n);
        };

        add(1);
        const needEllipsisLeft = currentPage > 3;
        const needEllipsisRight = currentPage < totalPages - 2;

        const middleSize = limit - 2 - (needEllipsisLeft ? 1 : 0) - (needEllipsisRight ? 1 : 0);

        let start = currentPage - Math.floor(middleSize / 2);
        let end = start + middleSize - 1;

        if (start < 2) {
            start = 2;
            end = start + middleSize - 1;
        }
        if (end > totalPages - 1) {
            end = totalPages - 1;
            start = end - middleSize + 1;
        }

        if (needEllipsisLeft && start > 2) add(-1);
        for (let p = start; p <= end; p++) add(p);
        if (needEllipsisRight && end < totalPages - 1) add(-1);

        add(totalPages);

        return out;
    });

    ngOnInit() {
        effect(
            () => {
                const maxPage = this.pages();
                const currentPage = this.currentPage();
                if (currentPage > maxPage) this.currentPage.set(maxPage);
            },
            { injector: this.injector },
        );
    }

    public updatePage(page: number) {
        if (page >= 1 && page <= this.pages() && page !== this.currentPage()) {
            this.currentPage.set(page);
        }
    }
}
